import type { ComboboxItem } from '@/components'
import { Combobox } from '@/components'
import { PlainLink } from '@/components_deprecated/Link'
import type { UserNode } from '@/modules/users'
import type { FormErrors, FormOnChangeFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useState } from 'react'
import styled from 'styled-components'
import type { ShallowCategory } from '.'
import { CategorySelect } from '.'
import { IssueDateRangeField } from './issue-date-range-field'
import { IssueRiskAspectCombobox } from './risk-aspect-combobox'
import { useIssueProcessField } from './use-issue-process-field'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import type { UseIssueFieldConfig } from '../field-config'
import { IssueFormField } from '../field-config'

const Card = styled.section`
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-card);

  /* On mobile the columns flatten (see the view's Content rule); lift this
     metadata card to sit directly after the title. */
  ${props => props.theme.media.custom({ max: '1100px' })} {
    order: -1;
  }
`

const Row: React.FC<
  React.PropsWithChildren<{ label: React.ReactNode; required?: boolean }>
> = ({ label, required, children }) => (
  <div className="space-y-1.5 border-t border-stone-200 px-5 py-3.5 first:border-t-0">
    <div className="flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </div>
    <div className="text-sm font-medium text-stone-900">{children}</div>
  </div>
)

interface CreateMainInfoProps {
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>
  updateForm: (values: Partial<EditIssueForm>) => void
  onChange: FormOnChangeFn<EditIssueForm>
  subcategoryOptions: ComboboxItem<string>[]
  fieldConfig: UseIssueFieldConfig
  /** Risk aspects are only required when the company has defined any. */
  hasRiskAspects: boolean
}

export const CreateMainInfo: React.FC<CreateMainInfoProps> = ({
  form,
  errors,
  updateForm,
  onChange,
  subcategoryOptions,
  fieldConfig,
  hasRiskAspects,
}) => {
  const translations = useTranslate({
    category: 'common.category',
    responsible: 'common.responsible',
    caseHandler: 'common.case-handler',
    date: 'common.date',
    severity: 'common.severity',
    riskAspect: 'common.risk-aspect',
    process: 'issues.process',
    subCategory: 'common.sub-category',
    select: 'common.select',
    none: 'common.none',
  })
  const severityOptions = useIssueSeverityOptions()
  const processField = useIssueProcessField(form.processChoice)

  const [temporaryResponsible, setTemporaryResponsible] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  const [temporaryCaseHandler, setTemporaryCaseHandler] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({
      category: id,
      subcategory: null,
    })

    setTemporaryResponsible(category?.responsibleUser ?? null)
    setTemporaryCaseHandler(category?.caseHandler ?? null)
  }

  return (
    <Card>
      <Row label={translations.category} required>
        <CategorySelect
          value={form.category}
          error={errors.category}
          placeholder={translations.select}
          onChange={handleChangeCategory}
        />
        {form.category &&
          subcategoryOptions.length > 0 &&
          fieldConfig.isVisible(IssueFormField.SUBCATEGORY, 'create') && (
            <div className="mt-2">
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
                {fieldConfig.labelFor(
                  IssueFormField.SUBCATEGORY,
                  translations.subCategory
                )}
                {fieldConfig.isRequired(
                  IssueFormField.SUBCATEGORY,
                  'create'
                ) && <span className="text-rose-500">*</span>}
              </div>
              <Combobox<string>
                mode="single"
                value={form.subcategory ?? ''}
                items={subcategoryOptions}
                clearable
                onChange={value => onChange('subcategory')(value || null)}
              />
              {errors.subcategory && (
                <p className="mt-1 text-xs text-rose-500">
                  {errors.subcategory}
                </p>
              )}
            </div>
          )}
      </Row>

      {fieldConfig.isVisible(IssueFormField.SEVERITY, 'create') && (
        <Row
          label={fieldConfig.labelFor(
            IssueFormField.SEVERITY,
            translations.severity
          )}
          required={fieldConfig.isRequired(IssueFormField.SEVERITY, 'create')}
        >
          <Combobox<string>
            mode="single"
            value={form.severity ?? ''}
            items={severityOptions}
            clearable
            onChange={value =>
              onChange('severity')((value || null) as EditIssueForm['severity'])
            }
          />
        </Row>
      )}

      {fieldConfig.isVisible(IssueFormField.RISK_ASPECT, 'create') && (
        <Row
          label={fieldConfig.labelFor(
            IssueFormField.RISK_ASPECT,
            translations.riskAspect
          )}
          required={
            hasRiskAspects &&
            fieldConfig.isRequired(IssueFormField.RISK_ASPECT, 'create')
          }
        >
          <IssueRiskAspectCombobox
            value={form.riskAspect}
            onChange={onChange('riskAspect')}
          />
        </Row>
      )}

      {processField.hasProcesses &&
        fieldConfig.isVisible(IssueFormField.PROCESS_CHOICE, 'create') && (
          <Row
            label={fieldConfig.labelFor(
              IssueFormField.PROCESS_CHOICE,
              translations.process
            )}
            required={fieldConfig.isRequired(
              IssueFormField.PROCESS_CHOICE,
              'create'
            )}
          >
            <Combobox<string>
              mode="single"
              value={form.processChoice ?? ''}
              items={processField.options}
              clearable
              onChange={value => onChange('processChoice')(value || null)}
            />
          </Row>
        )}

      {fieldConfig.isVisible(IssueFormField.RESPONSIBLE_USER, 'create') && (
        <Row label={translations.responsible}>
          {temporaryResponsible ? (
            <PlainLink
              to={`/users/${temporaryResponsible.id}`}
              $hoverColor="secondary"
            >
              {temporaryResponsible.fullName}
            </PlainLink>
          ) : (
            <span className="text-stone-500">{translations.none}</span>
          )}
        </Row>
      )}

      {fieldConfig.isVisible(IssueFormField.CASE_HANDLER, 'create') && (
        <Row label={translations.caseHandler}>
          {temporaryCaseHandler ? (
            <PlainLink
              to={`/users/${temporaryCaseHandler.id}`}
              $hoverColor="secondary"
            >
              {temporaryCaseHandler.fullName}
            </PlainLink>
          ) : (
            <span className="text-stone-500">{translations.none}</span>
          )}
        </Row>
      )}

      <Row label={translations.date}>
        <IssueDateRangeField
          start={form.date}
          end={form.dateEnd}
          onChange={(date, dateEnd) => updateForm({ date, dateEnd })}
        />
      </Row>
    </Card>
  )
}
